import { TriangleAlert } from '@sortlist-frontend/design-system/icons';

import { EditorialBlocks } from '_features/RankingPage/components/EditorialBlocks';
import { RichText } from '_features/RankingPage/components/RichText';

import type { GuideBlock, GuideLabels } from '../types';
import { BillingUnit } from '../widgets/BillingUnit';
import { BriefBuilder } from '../widgets/BriefBuilder';
import { Checklist } from '../widgets/Checklist';
import { DecisionTree } from '../widgets/DecisionTree';
import { ExecutionCalculator } from '../widgets/ExecutionCalculator';
import { ExpertQuiz } from '../widgets/ExpertQuiz';
import { isSharedBlock } from '../widgets/GuideBlockList';
import { GuideTabs } from '../widgets/GuideTabs';
import { PromptPanel } from '../widgets/PromptPanel';
import { RobotsBuilder } from '../widgets/RobotsBuilder';
import { RoiCalculator } from '../widgets/RoiCalculator';
import { ScopeSimulator } from '../widgets/ScopeSimulator';
import { SeatsComparator } from '../widgets/SeatsComparator';
import { Timeline } from '../widgets/Timeline';

interface GuideBlocksProps {
  blocks: readonly GuideBlock[];
  locale: string;
  serviceName: string;
  path: string;
  labels: GuideLabels;
}

export const GuideBlocks = (props: GuideBlocksProps) => {
  const { blocks, locale, serviceName, path, labels } = props;

  return (
    <div className="space-y-6">
      {blocks.map((block, index) => {
        const key = `${index}-${block.type}`;

        if (isSharedBlock(block)) {
          return <EditorialBlocks key={key} blocks={[block]} locale={locale} serviceName={serviceName} path={path} />;
        }

        switch (block.type) {
          case 'tabs':
            return (
              <GuideTabs
                key={key}
                label={block.label}
                variant={block.variant}
                tabs={block.tabs}
                locale={locale}
                serviceName={serviceName}
                path={path}
              />
            );
          case 'scale':
            return (
              <div key={key} className="rounded-2xl border border-primary-200 bg-primary-50 p-4 md:p-5">
                <div className="mb-2 flex justify-between text-xs font-semibold text-secondary-700">
                  <span>{block.left}</span>
                  <span>{block.right}</span>
                </div>
                <div className="h-2 rounded-full bg-gradient-to-r from-primary-200 to-primary-600" aria-hidden="true" />
              </div>
            );
          case 'decisionTree':
            return <DecisionTree key={key} label={block.label} tree={block.tree} labels={labels} />;
          case 'checklist':
            return <Checklist key={key} items={block.items} done={block.done} rest={block.rest} empty={block.empty} />;
          case 'quiz':
            return (
              <ExpertQuiz
                key={key}
                legend={block.legend}
                items={block.items}
                countLabel={block.countLabel}
                tiers={block.tiers}
                cta={block.cta}
              />
            );
          case 'seats':
            return (
              <SeatsComparator
                key={key}
                label={block.label}
                unit={block.unit}
                min={block.min}
                max={block.max}
                value={block.value}
                plans={block.plans}
                footnote={block.footnote}
                locale={locale}
                labels={labels}
              />
            );
          case 'roi':
            return (
              <RoiCalculator key={key} fields={block.fields} outputs={block.outputs} locale={locale} labels={labels} />
            );
          case 'simulator':
            return <ScopeSimulator key={key} fields={block.fields} locale={locale} labels={labels} />;
          case 'calculator':
            return (
              <ExecutionCalculator
                key={key}
                rows={block.rows}
                types={block.types}
                verdicts={block.verdicts}
                ticks={block.ticks}
                addLabel={block.addLabel}
                totalLabel={block.totalLabel}
                footnote={block.footnote}
                locale={locale}
                labels={labels}
              />
            );
          case 'billingUnit':
            return (
              <BillingUnit
                key={key}
                label={block.label}
                min={block.min}
                max={block.max}
                value={block.value}
                runs={block.runs}
                lines={block.lines}
                note={block.note}
                locale={locale}
                labels={labels}
              />
            );
          case 'briefBuilder':
            return (
              <BriefBuilder
                key={key}
                fields={block.fields}
                outTitle={block.outTitle}
                copyLabel={block.copyLabel}
                cta={block.cta}
                placeholder={block.placeholder}
                labels={labels}
              />
            );
          case 'robots':
            return (
              <RobotsBuilder
                key={key}
                agents={block.agents}
                outTitle={block.outTitle}
                copyLabel={block.copyLabel}
                labels={labels}
              />
            );
          case 'promptPanel':
            return (
              <PromptPanel
                key={key}
                fields={block.fields}
                templates={block.templates}
                copyLabel={block.copyLabel}
                listLabel={block.listLabel}
                rateLabel={block.rateLabel}
                labels={labels}
              />
            );
          case 'timeline':
            return <Timeline key={key} items={block.items} />;
          case 'callout':
            return (
              <p
                key={key}
                className="flex gap-3 rounded-2xl border border-warning-200 bg-warning-50 p-4 text-sm leading-relaxed text-secondary-700">
                <TriangleAlert size={18} className="mt-0.5 shrink-0 text-warning-500" aria-hidden="true" />
                <span>
                  <RichText text={block.text} />
                </span>
              </p>
            );
          case 'quote':
            return (
              <figure key={key} className="rounded-2xl border border-primary-200 bg-primary-50 p-5 md:p-6">
                <blockquote className="text-lg italic leading-relaxed text-secondary-950">
                  <RichText text={block.text} />
                </blockquote>
                {block.cite != null ? (
                  <figcaption className="mt-3 text-sm text-secondary-500">
                    {block.citeHref != null ? (
                      <a href={block.citeHref} rel="noopener" className="underline underline-offset-2">
                        {block.cite}
                      </a>
                    ) : (
                      block.cite
                    )}
                  </figcaption>
                ) : null}
              </figure>
            );
        }
      })}
    </div>
  );
};
